import React, { useCallback, useEffect, useState } from "react";
import { MAPBOX_TOKEN } from "../../config/mapbox";
import { SUPERMARKETS_BY_ID } from "../../data/supermarkets";
import { geocodePostcode } from "../../util/mapboxGeocode";
import {
  fetchRoute,
  profileForCommuteMode,
} from "../../util/mapboxDirections";
import { findSupermarketsNear } from "../../util/mapboxSupermarkets";
import {
  loadLivingPreferences,
  saveLivingPreferences,
} from "../../util/livingPreferencesStorage";
import { normalizeUkPostcode } from "../../util/ukPostcode";
import LivingToolsContent from "./LivingToolsContent";
import {
  CostOfLivingFormState,
  MyDayLeg,
  MyDayPlan,
  PropertyLivingProperty,
  ResolvedStop,
} from "./types";

export type { PropertyLivingProperty };

export interface PropertyLivingSectionProps {
  property: PropertyLivingProperty;
}

async function stopFromPostcode(
  id: string,
  label: string,
  type: ResolvedStop["type"],
  postcode: string,
  proximity: [number, number]
): Promise<ResolvedStop> {
  const normalized = normalizeUkPostcode(postcode);
  const geo = await geocodePostcode(normalized, { proximity });
  if (!geo) {
    throw new Error(`Could not find location for postcode ${normalized}.`);
  }
  return {
    id,
    label,
    name: geo.name,
    address: geo.fullAddress,
    longitude: geo.longitude,
    latitude: geo.latitude,
    type,
  };
}

const PropertyLivingSection: React.FC<PropertyLivingSectionProps> = ({
  property,
}) => {
  const [form, setForm] = useState<CostOfLivingFormState>(() =>
    loadLivingPreferences()
  );
  const [plan, setPlan] = useState<MyDayPlan | null>(null);
  const [isPlanning, setIsPlanning] = useState(false);
  const [planError, setPlanError] = useState("");
  const [showResults, setShowResults] = useState(false);

  useEffect(() => {
    setPlan(null);
    setShowResults(false);
    setPlanError("");
  }, [property.id]);

  const handleFormChange = useCallback((next: CostOfLivingFormState) => {
    setForm(next);
    saveLivingPreferences(next);
  }, []);

  const buildPlan = useCallback(async () => {
    setIsPlanning(true);
    setPlanError("");
    try {
      const proximity: [number, number] = [
        property.longitude,
        property.latitude,
      ];

      const home: ResolvedStop = {
        id: "home",
        label: "Home",
        name: "Property",
        longitude: property.longitude,
        latitude: property.latitude,
        type: "home",
      };

      const schedule = {
        arriveByTime: form.arriveByTime,
        leaveAtTime: form.leaveAtTime,
        schoolDropoffTime: form.schoolDropoffTime,
        schoolPickupTime: form.schoolPickupTime,
      };

      let primaryStop: ResolvedStop | null = null;
      let schoolStop: ResolvedStop | null = null;
      let workStop: ResolvedStop | null = null;

      if (form.daysPerWeek > 0) {
        if (form.dayType === "work") {
          primaryStop = await stopFromPostcode(
            "work",
            "Work",
            "work",
            form.primaryPostcode,
            proximity
          );
        } else if (form.dayType === "student") {
          primaryStop = await stopFromPostcode(
            "school",
            "School",
            "school",
            form.primaryPostcode,
            proximity
          );
        } else {
          schoolStop = await stopFromPostcode(
            "school",
            "School",
            "school",
            form.primaryPostcode,
            proximity
          );
          if (form.workPostcode.trim()) {
            workStop = await stopFromPostcode(
              "work",
              "Work",
              "work",
              form.workPostcode,
              proximity
            );
          }
        }
      }

      const extraStops: ResolvedStop[] = [];
      for (let i = 0; i < form.extraStops.length; i += 1) {
        const extra = form.extraStops[i];
        if (!extra.label.trim() || !extra.postcode.trim()) continue;
        extraStops.push(
          await stopFromPostcode(
            `extra-${i}`,
            extra.label.trim(),
            "extra",
            extra.postcode,
            proximity
          )
        );
      }

      const selectedBrands = form.selectedSupermarketIds
        .map((id) => SUPERMARKETS_BY_ID[id])
        .filter(Boolean);
      const supermarkets = await findSupermarketsNear(
        property.latitude,
        property.longitude,
        selectedBrands,
        MAPBOX_TOKEN
      );
      const primarySupermarket = supermarkets[0] ?? null;

      let shopStop: ResolvedStop | null = null;
      if (primarySupermarket) {
        shopStop = {
          id: "shop",
          label: "Shop",
          name: primarySupermarket.name,
          address: primarySupermarket.address,
          longitude: primarySupermarket.longitude,
          latitude: primarySupermarket.latitude,
          type: "shop",
        };
      }

      const profile = profileForCommuteMode(form.commuteMode);
      const legs: MyDayLeg[] = [];
      let totalDurationSeconds = 0;
      let totalDistanceMeters = 0;

      const addLeg = async (
        from: ResolvedStop,
        to: ResolvedStop,
        phase: "morning" | "evening"
      ) => {
        const route = await fetchRoute(
          { longitude: from.longitude, latitude: from.latitude },
          { longitude: to.longitude, latitude: to.latitude },
          profile
        );
        legs.push({ from, to, route, phase });
        totalDurationSeconds += route?.durationSeconds ?? 0;
        totalDistanceMeters += route?.distanceMeters ?? 0;
      };

      if (form.daysPerWeek > 0) {
        if (form.dayType === "work" && primaryStop) {
          await addLeg(home, primaryStop, "morning");
        } else if (form.dayType === "student" && primaryStop) {
          await addLeg(home, primaryStop, "morning");
        } else if (form.dayType === "parent" && schoolStop) {
          await addLeg(home, schoolStop, "morning");
          if (workStop) {
            await addLeg(schoolStop, workStop, "morning");
          }
        }

        let eveningFrom: ResolvedStop | null = null;
        if (form.dayType === "work" && primaryStop) {
          eveningFrom = primaryStop;
        } else if (form.dayType === "student" && primaryStop) {
          eveningFrom = primaryStop;
        } else if (form.dayType === "parent") {
          if (workStop) {
            await addLeg(workStop, schoolStop!, "evening");
            eveningFrom = schoolStop;
          } else {
            eveningFrom = schoolStop;
          }
        }

        if (eveningFrom) {
          let current = eveningFrom;
          for (const extra of extraStops) {
            await addLeg(current, extra, "evening");
            current = extra;
          }
          if (shopStop) {
            await addLeg(current, shopStop, "evening");
            current = shopStop;
          }
          await addLeg(current, home, "evening");
        }
      } else {
        let current: ResolvedStop = home;
        for (const extra of extraStops) {
          await addLeg(current, extra, "morning");
          current = extra;
        }
        if (shopStop) {
          await addLeg(current, shopStop, "morning");
          current = shopStop;
        }
        if (current.id !== "home") {
          await addLeg(current, home, "evening");
        }
      }

      const orderedStops: ResolvedStop[] = [home];
      for (const leg of legs) {
        if (!orderedStops.find((s) => s.id === leg.to.id)) {
          orderedStops.push(leg.to);
        }
      }

      setPlan({
        dayType: form.dayType,
        schedule,
        stops: orderedStops,
        legs,
        supermarkets,
        primarySupermarket,
        totalDurationSeconds,
        totalDistanceMeters,
      });
      setShowResults(true);
    } catch (err) {
      console.error(err);
      setPlanError(
        err instanceof Error ? err.message : "Failed to plan your day."
      );
    } finally {
      setIsPlanning(false);
    }
  }, [form, property]);

  return (
    <section id="living-here">
      <h2 className="section-title">Living here</h2>
      <LivingToolsContent
        property={property}
        form={form}
        onFormChange={handleFormChange}
        onPlanMyDay={buildPlan}
        isPlanning={isPlanning}
        planError={planError}
        plan={plan}
        showResults={showResults}
        variant="inline"
      />
    </section>
  );
};

export default PropertyLivingSection;
